// What a screen reader is told (issue 1175): one polite live region for the whole shell. It says only what this
// person's screen shows them — a new roll in their OWN log as the log shows it (the player's version, never more),
// the turn change as the strip shows it ("Shelby's turn", "Your turn"; nothing while something unseen acts), every
// notice the table sets (a refusal: "A wall is in the way."), and what the Tokens list says of a keyboard move. It
// computes nothing from data the screen was not given: every sentence is built from the same projected state the
// panels draw.
import { useEffect, useRef, useState } from 'react';
import { dmEncounterView, emptyEncounter, type Encounter, type PlayerEncounter } from '@dragons-table/vtt-shared';
import type { LogEntry } from '../dice';
import { useTableState } from '../table/TableState';
import { useShell } from './context';
import { logEntrySpeech, logNamer } from './panels/DicePanel';

type Listener = (text: string) => void;
const listeners = new Set<Listener>();

/** Say something through the live region (the Tokens list's moves). A module emitter like the board's bus. */
export function announce(text: string): void {
  if (!text) return;
  for (const fn of listeners) fn(text);
}

/**
 * The entries a log gained at its END since `before` (issue 1175): what arrived live. A whole-page load replaces every
 * entry and an older page is put in front, so neither is new; an entry changed in place (an amendment) is not new.
 */
export function arrivedEntries(before: readonly LogEntry[], after: readonly LogEntry[]): LogEntry[] {
  if (before.length === 0) return after.length > 0 ? [...after] : [];
  const last = before[before.length - 1]!;
  const at = after.lastIndexOf(last);
  return at < 0 ? [] : after.slice(at + 1);
}

/** The turn as the strip shows it to this viewer: null when no fight runs, or while something they cannot see acts. */
export function turnSpeech(reading: PlayerEncounter, isDm: boolean): { key: string; text: string } | null {
  if (!reading.running || reading.active === null) return null;
  const c = reading.combatants.find((x) => x.id === reading.active);
  if (!c) return null;
  return { key: `${reading.round}:${c.id}`, text: c.mine && !isDm ? 'Your turn.' : `${c.name ?? 'Unnamed'}'s turn.` };
}

export function Announcer() {
  const { table } = useShell();
  const { log, logLoads, notice, noticeId, store, role, meId } = useTableState();
  const [said, setSaid] = useState<{ text: string; n: number }>({ text: '', n: 0 });
  // What one change brings is said together ("Your turn. It's your turn — Shelby."): sentences from the same commit are
  // joined, a repeat dropped; the batch closes in the last effect below.
  const batch = useRef<string[] | null>(null);
  const say = useRef((text: string) => {
    if (batch.current?.includes(text)) return;
    batch.current = [...(batch.current ?? []), text];
    const all = batch.current.join(' ');
    setSaid((cur) => ({ text: all, n: cur.n + 1 }));
  });

  useEffect(() => {
    const fn: Listener = (text) => say.current(text);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);

  // The log: what arrived since the last look, unless the log was (re)loaded in between.
  const seen = useRef<{ log: readonly LogEntry[]; loads: number | undefined }>({ log, loads: logLoads });
  useEffect(() => {
    const before = seen.current;
    seen.current = { log, loads: logLoads };
    if (before.loads !== logLoads || before.log === log) return;
    const nameOf = logNamer(meId, table.members, store.state.objects);
    const lines = arrivedEntries(before.log, log).map((e) => logEntrySpeech(e, meId, nameOf, table.members)).filter((t): t is string => !!t);
    if (lines.length) say.current(lines.join(' '));
  }, [log, logLoads]); // eslint-disable-line react-hooks/exhaustive-deps

  // The turn: said when it changes, never on arrival.
  const raw = (store.state.encounter ?? emptyEncounter()) as Encounter | PlayerEncounter;
  const isDm = role === 'dm';
  const turn = turnSpeech(isDm ? dmEncounterView(raw as Encounter) : (raw as PlayerEncounter), isDm);
  const lastTurn = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const was = lastTurn.current;
    lastTurn.current = turn?.key ?? null;
    if (was === undefined || !turn || turn.key === was) return;
    say.current(turn.text);
  }, [turn?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // A notice (a refusal, "Ogre is yours now."): the board shows it; this says it.
  const lastNotice = useRef(noticeId);
  useEffect(() => {
    if (noticeId === lastNotice.current) return;
    lastNotice.current = noticeId;
    if (notice) say.current(notice);
  }, [noticeId, notice]);

  useEffect(() => { batch.current = null; });

  // A new node for each sentence, so the same words said twice are spoken twice.
  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only" data-testid="announcer">
      {said.text && <span key={said.n}>{said.text}</span>}
    </div>
  );
}
